import type { PayFigures, PayrollQueue, PayrollRow } from '@myrewards/contracts';
import { formatMoney, formatShortDate, plural } from '../../ui';
import { buysShares, PAYS_AS } from '../labels';

// APP-117: Payment and statement work share one row; completion is kept in history. Before paying, a PAA's tax is the Stock Admin team's
// estimate and a cash-only line has none (APP-101); after, the row shows Payroll's own numbers.

/** The cash still to pay, before tax ("$18,000 gross"). */
export function grossToPay(queue: PayrollQueue): number {
  return queue.rows.filter((r) => r.status === 'to_pay').reduce((n, r) => n + r.grossCashCents, 0);
}

/**
 * What reaches the employee before Payroll sets the final tax. A cash line is the cash less the
 * tax being entered; a choice that buys stock keeps the Stock Admin's estimate (the share count
 * is worked out by the server from the final tax). Null before a cash-only line has a tax.
 */
export function netBeforePay(row: PayrollRow, draftTaxCents: number | null): PayFigures | null {
  if (buysShares(row.payAs) || draftTaxCents === null) return row.estimate;
  return {
    taxCents: draftTaxCents,
    cashCents: Math.max(0, row.grossCashCents - draftTaxCents),
    shares: 0,
  };
}

/** A problem with the final tax being entered, or null when it can be paid. */
export function finalTaxProblem(row: PayrollRow, draftTaxCents: number | null): string | null {
  if (draftTaxCents === null) return 'Enter the final tax.';
  if (draftTaxCents < 0) return 'The final tax can’t be below zero.';
  if (draftTaxCents > row.grossCashCents) return 'The final tax can’t be more than the cash.';
  return null;
}

/**
 * Where the estimate came from (APP-101). A PAA choice uses the tax the Stock Admin team entered
 * from ADP Quick Calc, the figure on the letter; a cash-only line has no app estimate.
 */
export function estimateSource(row: PayrollRow): { title: string; detail: string } {
  if (row.payAs === 'cash' || row.estimate === null)
    return {
      title: 'Payroll sets it',
      detail: 'A cash-only award has no app estimate: the tax is yours to set from the pay run.',
    };
  const choice = row.payAs === 'closed_as_cash' ? 'choice (a)' : `choice (${row.payAs})`;
  return {
    title: `Tax ${formatMoney(row.estimate.taxCents)}`,
    detail: `The Stock Admin team’s figure from ADP Quick Calc, for ${choice}.`,
  };
}

/** The employee's choice in a sentence, for the line's details. */
export function choiceWords(row: PayrollRow): { title: string; detail: string } {
  const words = PAYS_AS[row.payAs];
  if (row.payAs === 'cash')
    return { title: 'Cash only', detail: 'No election: the whole amount is paid as cash.' };
  const est = row.estimate;
  const figures =
    est === null
      ? null
      : est.shares > 0
        ? `${plural(est.shares, 'share', 'shares')} + ${formatMoney(est.cashCents)} cash`
        : `${formatMoney(est.cashCents)} cash`;
  const choice = words.label.replace('PAA · ', '');
  return {
    title: figures === null ? choice : `${choice}: ${figures}`,
    detail: buysShares(row.payAs)
      ? 'Shares are bought with what’s left after your final tax, in whole shares; the rest is paid in cash. A different final tax can change the share count.'
      : words.detail,
  };
}

/** "Paid Oct 9", "Oct 16" (award date), or "Award date not set". */
/**
 * The Pay date cell: the day it was paid, or "Not paid yet" with the award date under it. There's
 * no pay calendar to say the next pay date (APP-067 §12), so an unpaid line never shows a date
 * that reads like one (APP-067 §13).
 */
export function payDateWords(row: PayrollRow): { text: string; sub: string | null } {
  if (row.paidAt !== null) return { text: `Paid ${formatShortDate(row.paidAt)}`, sub: null };
  return {
    text: 'Not paid yet',
    sub:
      row.awardDate === null
        ? 'Award date not set'
        : `Award date ${formatShortDate(row.awardDate)}`,
  };
}

/** The charge code as Payroll reads it: account · org, and the project when there is one. */
export function codingWords(row: PayrollRow): string {
  if (row.coding === null) return 'Not coded yet';
  const parts = [row.coding.account, `org ${row.coding.org}`];
  parts.push(row.coding.project === null ? 'no project' : `project ${row.coding.project}`);
  return parts.join(' · ');
}

/** The work a list holds, for its card's sentence: "2 lines to pay and 1 statement to attach." */
export function queueSentence(counts: PayrollQueue['counts']): string {
  const toPay = counts.toPay;
  const due = counts.statementsDue;
  if (toPay === 0 && due === 0) return 'Nothing to pay and no statements to attach.';
  const parts: string[] = [];
  if (toPay > 0) parts.push(`${plural(toPay, 'line', 'lines')} to pay`);
  if (due > 0) parts.push(`${plural(due, 'statement', 'statements')} to attach`);
  return `${parts.join(' and ')}.`;
}

/** APP-248: whether any Spot Award is in progress, so the page gives them their own section. */
export function hasSpotSection(queue: PayrollQueue): boolean {
  return queue.spot.toPay + queue.spot.statementsDue > 0;
}

/** APP-248: the other awards' share of the work, once the Spot Awards have a section of their own. */
export function otherCounts(queue: PayrollQueue): PayrollQueue['counts'] {
  return {
    toPay: Math.max(0, queue.counts.toPay - queue.spot.toPay),
    statementsDue: Math.max(0, queue.counts.statementsDue - queue.spot.statementsDue),
  };
}

/**
 * APP-248 (S8): the Spot section's total still to pay, "3 awards · $2,750 · from the Spot Awards
 * pool", or null when every Spot Award in it is paid. APP-267: it names the pools that really pay
 * it, with each one's share when there's more than one: an older Spot Award keeps the pool it was
 * made from.
 */
export function spotTotalWords(spot: PayrollQueue['spot']): string | null {
  if (spot.awardsToPay === 0) return null;
  const total = `${plural(spot.awardsToPay, 'award', 'awards')} · ${formatMoney(spot.cashToPayCents)}`;
  const [only] = spot.paidFrom;
  if (only === undefined) return total;
  const paidFrom =
    spot.paidFrom.length === 1
      ? `from the ${only.pool.name} pool`
      : spot.paidFrom
          .map((p) => `${formatMoney(p.cashCents)} from the ${p.pool.name} pool`)
          .join(', ');
  return `${total} · ${paidFrom}`;
}
